import { Component, Input, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TipoProductoService } from '../services/tipo-producto.service';
import { debounceTime, firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { TipoProductoId } from '../assets/columnas-producto.constants';

@Component({
    selector: 'app-table-carga-masiva',
    templateUrl: './table-carga-masiva.component.html',
    styleUrls: ['./table-carga-masiva.component.css'],
})
export class TableCargaMasivaComponent {
    @Input() columnas: any[] = [];
    @Input() tipoProducto;
    productosForm: FormGroup;
    displayedColumns: string[] = [];
    empaquePresentaciones: any[] = [];
    lastSelectedEmpaque = '';
    codigoSeleccionado = '';
    codigoRepetido: boolean;
    erroresProductos: any[] = [];
    isFormInvalid = true;

    /**
     * Indica si el tipo de producto seleccionado es Nutrición animal (id 4).
     * Se evalúa por id y no por nombre para no depender del texto visible.
     */
    get esNutricionAnimal(): boolean {
        return String(this.tipoProducto) === TipoProductoId.NutricionAnimal;
    }

    constructor(
        private fb: FormBuilder,
        private tipoProductoService: TipoProductoService
    ) {
        this.productosForm = this.fb.group({
            productos: this.fb.array([]),
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.columnas) {
            this.actualizarColumnas();
        }
    }

    async actualizarColumnas() {
        this.displayedColumns = this.columnas.map((columna) => columna.campo);
        this.productos.clear();
        this.agregarProducto();

        // Cargar los empaques correspondientes al tipo de producto actual
        try {
            this.empaquePresentaciones =
                await this.tipoProductoService.loadEmpaquesPresentacion(
                    this.tipoProducto
                );
        } catch (error) {
            console.error('Error al cargar las opciones de empaques:', error);
            this.empaquePresentaciones = [];
        }
    }

    async ngOnInit() {
        this.empaquePresentaciones =
            await this.tipoProductoService.loadEmpaquesPresentacion(
                this.tipoProducto
            );
        this.displayedColumns = this.columnas.map((columna) => columna.campo);

        this.productosForm.valueChanges.subscribe(() => {
            this.validarFormulario();
        });
    }

    async validarFormulario() {
        const tieneErrores = await this.erroresProductos.some(
            (error) => error.codigoRepetidoProducto
        );
        this.isFormInvalid = this.productosForm.invalid || tieneErrores;
    }

    crearProducto(): FormGroup {
        const productoGroup: any = {};
        this.columnas.forEach((columna) => {
            const validators = columna.validaciones || [];
            productoGroup[columna.campo] = ['', validators];
        });
        productoGroup['presentaciones'] = this.fb.array([
            this.crearPresentacion(),
        ]);
        return this.fb.group(productoGroup);
    }

    crearPresentacion(): FormGroup {
        let empaque = '';
        let cantidad: number | null = null;
        let unidadMedidaEquivalente = '';
        let alimentosDisabled = false;

        if (this.esNutricionAnimal) {
            empaque = '5';
            cantidad = 40;
            unidadMedidaEquivalente = '01';
            alimentosDisabled = true;
        }

        const presentacionForm = this.fb.group({
            empaque: [
                { value: empaque, disabled: alimentosDisabled },
                Validators.required,
            ],
            cantidad: [
                { value: cantidad, disabled: alimentosDisabled },
                [Validators.required],
            ],
            empaqueUnidadMedidaId: [
                { value: unidadMedidaEquivalente, disabled: alimentosDisabled },
                [Validators.required, Validators.min(1)],
            ],
            codigoPresentacion: ['', [Validators.required, Validators.min(1)]],
            unidadesDeMedida: [[]],
        });

        presentacionForm.get('empaque')?.valueChanges.subscribe((empaqueId) => {
            this.cargarUnidadMedidaSeleccionada(empaqueId, presentacionForm);
        });

        if (this.esNutricionAnimal) {
            this.cargarUnidadMedidaSeleccionada('5', presentacionForm);
            this.updateCodeUnitMeasurement(presentacionForm);
        }

        const actualizarUnidadMedida = () => {
            this.updateCodeUnitMeasurement(presentacionForm);
        };

        const empaqueControl = presentacionForm.get('empaque');
        if (empaqueControl) {
            empaqueControl.valueChanges.subscribe(actualizarUnidadMedida);
        }

        const cantidadControl = presentacionForm.get('cantidad');
        if (cantidadControl) {
            cantidadControl.valueChanges.subscribe(actualizarUnidadMedida);
        }

        const unidadesControl = presentacionForm.get('unidadesDeMedida');
        if (unidadesControl) {
            unidadesControl.valueChanges.subscribe(actualizarUnidadMedida);
        }

        return presentacionForm;
    }

    // Cargar las unidades de medida solo para la presentación seleccionada
    async cargarUnidadMedidaSeleccionada(
        empaqueId: string,
        presentacionForm: FormGroup
    ) {
        try {
            const response = await firstValueFrom(
                this.tipoProductoService.cargarUnidadMedida(empaqueId)
            );
            const unidadesDeMedida = response;
            presentacionForm
                .get('unidadesDeMedida')
                ?.setValue(unidadesDeMedida);
            if (unidadesDeMedida.length > 0) {
                const unidadMedida = response[0];
                this.codigoSeleccionado = unidadesDeMedida[0].codigo;
                // Establecer el valor por defecto
                presentacionForm
                    .get('empaqueUnidadMedidaId')
                    ?.setValue(unidadMedida.EmpaqueUnidadMedida.id);
                this.tipoProductoService.updateCodeUnitMeasurement(
                    presentacionForm,
                    this.codigoSeleccionado
                );
            }
        } catch (error) {
            console.error('Error cargando unidades de medida', error);
        }
    }

    onUnidadMedidaChange(
        event: Event,
        productoIndex: number,
        presentacionIndex: number
    ) {
        const selectedOption = (event.target as HTMLSelectElement)
            .selectedOptions[0];
        this.codigoSeleccionado =
            selectedOption.getAttribute('data-codigo') || '';
        this.tipoProductoService.onUnidadMedidaChange(
            event,
            productoIndex,
            presentacionIndex,
            this.productos
        );
        this.onCambio(event, productoIndex, presentacionIndex);
    }

    updateCodeUnitMeasurement(presentacionForm: FormGroup) {
        this.tipoProductoService.updateCodeUnitMeasurement(
            presentacionForm,
            this.codigoSeleccionado
        );
    }

    agregarProducto() {
        this.productos.push(this.crearProducto());
    }

    eliminarProducto(index: number) {
        this.productos.removeAt(index);
    }

    agregarPresentacion(productoIndex: number) {
        const producto = this.productos.at(productoIndex);
        const presentaciones = producto.get('presentaciones') as FormArray;
        presentaciones.push(this.crearPresentacion());
    }

    eliminarPresentacion(productoIndex: number, presentacionIndex: number) {
        const producto = this.productos.at(productoIndex);
        const presentaciones = producto.get('presentaciones') as FormArray;
        presentaciones.removeAt(presentacionIndex);
    }

    getPresentaciones(productoIndex: number): FormArray {
        const producto = this.productos.at(productoIndex);
        return producto.get('presentaciones') as FormArray;
    }

    get productos(): FormArray {
        return this.productosForm.get('productos') as FormArray;
    }

    validacion(event: KeyboardEvent): void {
        this.tipoProductoService.validacion(event);
    }

    validarPresentacionRepetida(
        productoIndex: number,
        presentacionIndex: number
    ) {
        const producto = this.productos.at(productoIndex);
        const presentaciones = producto.get('presentaciones') as FormArray;

        // Obtener los valores actuales de la presentación
        const cantidad = presentaciones
            .at(presentacionIndex)
            .get('cantidad')?.value;
        const empaque = presentaciones
            .at(presentacionIndex)
            .get('empaque')?.value;
        const empaqueUnidadMedidaId = presentaciones
            .at(presentacionIndex)
            .get('empaqueUnidadMedidaId')?.value;
        const codigoPresentacion = presentaciones
            .at(presentacionIndex)
            .get('codigoPresentacion')?.value;

        // Verificación de que la presentación no esté vacía
        if (cantidad === '' || empaque === '' || empaqueUnidadMedidaId === '') {
            return true; // Si cantidad o empaque están vacíos, no se valida
        }

        // Buscar si existe alguna presentación repetida (excepto la actual)
        const codigoExistente = presentaciones.value.some(
            (presentacion: any, index: number) => {
                return (
                    presentacion.codigoPresentacion === codigoPresentacion &&
                    presentacion.empaqueUnidadMedidaId ===
                        empaqueUnidadMedidaId &&
                    index !== presentacionIndex // Excluir la presentación actual de la validación
                );
            }
        );

        if (codigoExistente) {
            Swal.fire(
                '',
                'Esta presentación ya existe en el producto actual',
                'error'
            );
            return false;
        }
        return true;
    }
    // Método para manejar el cambio en la cantidad y activar la validación
    onCambio(event: Event, productoIndex: number, presentacionIndex: number) {
        const presentacion =
            this.getPresentaciones(productoIndex).at(presentacionIndex);
        const codigoPresentacionControl =
            presentacion.get('codigoPresentacion');
        const esValida = this.validarPresentacionRepetida(
            productoIndex,
            presentacionIndex
        );
        if (codigoPresentacionControl.value) {
            if (!esValida) {
                codigoPresentacionControl.setErrors({ codigoRepetido: true });
            } else {
                codigoPresentacionControl.setErrors(null);
            }
        }
        this.productosForm.updateValueAndValidity();
        presentacion.updateValueAndValidity();
    }

    async productoDuplicado(event: any, productoIndex: number) {
        const productoControl = this.productos.at(productoIndex);
        const registroIca = productoControl?.get('numeroRegistroIca')?.value;

        if (!registroIca) return;

        this.erroresProductos[productoIndex] = {};
        for (let i = 0; i < this.productos.length; i++) {
            if (i !== productoIndex) {
                const otroRegistroIca = this.productos
                    .at(i)
                    ?.get('numeroRegistroIca')?.value;
                if (registroIca === otroRegistroIca) {
                    this.erroresProductos[productoIndex] = {
                        codigoRepetidoProducto: true,
                    };
                    Swal.fire(
                        '',
                        'Ya existe un producto con el mismo número de registro ICA en el formulario.',
                        'error'
                    );
                    this.validarFormulario();
                    return;
                }
            }
        }

        const mensaje =
            await this.tipoProductoService.validacionProductoDuplicado(
                registroIca
            );
        if (
            mensaje ===
            'Ya existe un producto con el mismo número de registro ICA y el mismo tipo de producto.'
        ) {
            this.erroresProductos[productoIndex] = {
                codigoRepetidoProducto: true,
            };
            Swal.fire('', mensaje, 'error');
        }

        this.validarFormulario();
    }

    onSubmit() {
        // verificacion si hay algún error en el arreglo
        if (
            this.erroresProductos.some((error) => error.codigoRepetidoProducto)
        ) {
            Swal.fire(
                '',
                'No puedes guardar el formulario con productos duplicados.',
                'error'
            );
            return;
        }
        const rawFormValue = this.productosForm.getRawValue();

        //this.tipoProductoService.activarPresentacion(this.productos);
        this.tipoProductoService.postReporteMasivo(
            rawFormValue,
            this.tipoProducto
        );
    }
}
